import React, { useEffect, useState } from "react";
import { Gamepad2, Sparkles, X } from "lucide-react";

/**
 * KidsGames — a Fun Zone arcade of safe mini-games.
 *
 * All games are bundled client-side (no external frames, no ads, no
 * tracking) so nothing leaks outside the walled garden. Each game is a
 * tiny component that runs inside the overlay — keeps kids happy and
 * keeps us safe.
 */
export default function KidsGames() {
  const [active, setActive] = useState(null);

  const GAMES = [
    { key: "memory",  label: "Memory Match",   emoji: "🧩", bg: "from-pink-400 via-fuchsia-500 to-purple-600",  component: MemoryMatch,  testid: "game-memory" },
    { key: "catch",   label: "Star Catcher",   emoji: "⭐", bg: "from-amber-400 via-orange-500 to-rose-500",     component: StarCatcher,  testid: "game-catch" },
    { key: "click",   label: "Tap the Fruit",  emoji: "🍎", bg: "from-emerald-400 via-teal-500 to-cyan-600",     component: TapFruit,     testid: "game-click" },
    { key: "paint",   label: "Color Fill",     emoji: "🎨", bg: "from-violet-500 via-purple-500 to-indigo-700", component: ColorFill,    testid: "game-paint" },
    { key: "spot",    label: "Spot the Odd",   emoji: "🔍", bg: "from-cyan-400 via-sky-500 to-blue-700",         component: SpotTheOdd,   testid: "game-spot" },
    { key: "math",    label: "Math Quick",     emoji: "➕", bg: "from-rose-400 via-red-500 to-orange-500",       component: MathQuick,    testid: "game-math" },
    { key: "word",    label: "Word Match",     emoji: "🔤", bg: "from-lime-400 via-green-500 to-emerald-600",    component: WordMatch,    testid: "game-word" },
    { key: "sim",     label: "Simon Says",     emoji: "🎯", bg: "from-fuchsia-500 via-pink-500 to-rose-400",     component: SimonSays,    testid: "game-sim" },
  ];

  const Active = active?.component;

  return (
    <div data-testid="kids-games-page" className="pb-20 px-3 sm:px-6 md:px-10 max-w-6xl mx-auto text-white">
      <div className="relative overflow-hidden rounded-3xl p-5 sm:p-8 mt-3 bg-gradient-to-br from-violet-500 via-purple-500 to-indigo-700 shadow-2xl ring-4 ring-white/25">
        <div className="absolute -top-8 -right-8 opacity-25 pointer-events-none text-[10rem]">🕹️</div>
        <div className="relative flex items-center gap-3 flex-wrap">
          <img src="/kids-learn-play-logo.png" alt="" className="w-20 sm:w-28 h-auto drop-shadow-2xl" />
          <div>
            <div className="inline-flex items-center gap-1 bg-white/20 text-white text-[10px] uppercase tracking-[0.25em] font-black px-2 py-0.5 rounded-full backdrop-blur"><Sparkles className="w-3 h-3" /> Kid Games</div>
            <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter drop-shadow mt-1">Play time!</h1>
            <p className="text-white/90 text-sm">Safe mini-games. No ads, no timers, no stress.</p>
          </div>
        </div>
      </div>

      <div data-testid="games-grid" className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {GAMES.map((g) => (
          <button
            key={g.key}
            data-testid={g.testid}
            onClick={() => setActive(g)}
            className={`relative overflow-hidden rounded-2xl aspect-square ring-4 ring-white/80 shadow-xl bg-gradient-to-br ${g.bg} text-white text-left p-4 flex flex-col justify-between hover:scale-[1.04] transition`}
          >
            <div aria-hidden className="absolute -top-6 -right-6 text-[8rem] opacity-30 pointer-events-none">{g.emoji}</div>
            <div className="relative z-10">
              <Gamepad2 className="w-6 h-6 opacity-80" />
            </div>
            <div className="relative z-10">
              <div className="text-[3rem] leading-none drop-shadow">{g.emoji}</div>
              <div className="font-display font-black text-lg mt-1">{g.label}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Game overlay */}
      {active && (
        <div data-testid="game-overlay" className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#0f0b20] rounded-3xl ring-4 ring-white/20 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/30">
              <div className="font-display font-black text-xl inline-flex items-center gap-2">
                <span className="text-2xl">{active.emoji}</span> {active.label}
              </div>
              <button data-testid="game-close" onClick={() => setActive(null)} className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {Active && <Active />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MINI-GAMES — each is a tiny self-contained component.
   ═══════════════════════════════════════════════════════════════════ */

function MemoryMatch() {
  const BASE = ["🦊", "🐰", "🐼", "🐸", "🐵", "🐱"];
  const [deck, setDeck] = useState(() => shuffle([...BASE, ...BASE]).map((e, i) => ({ id: i, emoji: e, flipped: false, matched: false })));
  const [pick, setPick] = useState([]);
  const [moves, setMoves] = useState(0);

  const flip = (card) => {
    if (card.flipped || card.matched || pick.length === 2) return;
    const next = deck.map((c) => c.id === card.id ? { ...c, flipped: true } : c);
    const sel = [...pick, { ...card, flipped: true }];
    setDeck(next); setPick(sel);
    if (sel.length === 2) {
      setMoves((m) => m + 1);
      setTimeout(() => {
        if (sel[0].emoji === sel[1].emoji) {
          setDeck((d) => d.map((c) => (c.id === sel[0].id || c.id === sel[1].id) ? { ...c, matched: true } : c));
        } else {
          setDeck((d) => d.map((c) => (c.id === sel[0].id || c.id === sel[1].id) ? { ...c, flipped: false } : c));
        }
        setPick([]);
      }, 700);
    }
  };

  const allMatched = deck.every((c) => c.matched);
  const reset = () => { setDeck(shuffle([...BASE, ...BASE]).map((e, i) => ({ id: i, emoji: e, flipped: false, matched: false }))); setPick([]); setMoves(0); };

  return (
    <div data-testid="game-memory-board" className="text-center">
      <div className="mb-3 flex items-center justify-center gap-4 text-sm text-white/80">
        <span>Moves: <b>{moves}</b></span>
        <button onClick={reset} className="bg-white/15 hover:bg-white/25 font-bold px-3 py-1 rounded-full text-xs">Reset</button>
      </div>
      <div className="grid grid-cols-4 gap-2 max-w-[360px] mx-auto">
        {deck.map((c) => (
          <button
            key={c.id}
            onClick={() => flip(c)}
            className={`aspect-square rounded-xl text-3xl font-black flex items-center justify-center transition ${c.matched ? "bg-emerald-400 opacity-70" : c.flipped ? "bg-gradient-to-br from-pink-400 to-fuchsia-500" : "bg-white/10 hover:bg-white/20"}`}
          >
            {(c.flipped || c.matched) ? c.emoji : "?"}
          </button>
        ))}
      </div>
      {allMatched && <div className="mt-4 text-2xl font-display font-black text-yellow-300">You did it! 🎉</div>}
    </div>
  );
}

function StarCatcher() {
  const [stars, setStars] = useState(() => genStars());
  const [score, setScore] = useState(0);

  const pop = (id) => {
    setStars((s) => s.filter((x) => x.id !== id));
    setScore((n) => n + 1);
    setTimeout(() => setStars((s) => [...s, newStar()]), 300);
  };

  return (
    <div data-testid="game-catch-board" className="text-center">
      <div className="mb-3 text-sm text-white/80">Score: <b className="text-yellow-300">{score}</b> — tap the stars!</div>
      <div className="relative w-full aspect-square max-w-[420px] mx-auto bg-gradient-to-br from-indigo-900 to-black rounded-2xl ring-2 ring-white/10 overflow-hidden">
        {stars.map((s) => (
          <button
            key={s.id}
            onClick={() => pop(s.id)}
            style={{ left: `${s.x}%`, top: `${s.y}%` }}
            className="absolute text-3xl hover:scale-125 transition"
            aria-label="star"
          >⭐</button>
        ))}
      </div>
    </div>
  );
}

function TapFruit() {
  const EMOJI = ["🍎", "🍓", "🍇", "🍊", "🥝", "🍑"];
  const [target, setTarget] = useState(EMOJI[0]);
  const [grid, setGrid] = useState(() => genGrid(EMOJI));
  const [score, setScore] = useState(0);

  const tap = (e, i) => {
    if (e === target) {
      setScore((n) => n + 1);
      setTarget(EMOJI[Math.floor(Math.random() * EMOJI.length)]);
      setGrid(genGrid(EMOJI));
    } else {
      setScore((n) => Math.max(0, n - 1));
    }
  };

  return (
    <div data-testid="game-click-board" className="text-center">
      <div className="text-white/80 text-sm">Tap all the <span className="text-3xl align-middle">{target}</span></div>
      <div className="mt-2 text-sm">Score: <b className="text-emerald-300">{score}</b></div>
      <div className="grid grid-cols-5 gap-2 mt-3 max-w-[360px] mx-auto">
        {grid.map((e, i) => (
          <button
            key={i}
            onClick={() => tap(e, i)}
            className="aspect-square text-3xl rounded-xl bg-white/10 hover:bg-white/20 transition"
          >{e}</button>
        ))}
      </div>
    </div>
  );
}

function ColorFill() {
  const COLORS = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899"];
  const [color, setColor] = useState(COLORS[0]);
  const [cells, setCells] = useState(Array(36).fill("#1f2937"));

  return (
    <div data-testid="game-paint-board" className="text-center">
      <div className="text-white/80 text-sm mb-2">Pick a color and tap the squares!</div>
      <div className="flex items-center justify-center gap-2 mb-3">
        {COLORS.map((c) => (
          <button
            key={c}
            onClick={() => setColor(c)}
            style={{ backgroundColor: c }}
            className={`w-8 h-8 rounded-full ring-2 ${color === c ? "ring-white" : "ring-white/20"}`}
          />
        ))}
        <button onClick={() => setCells(Array(36).fill("#1f2937"))} className="ml-2 bg-white/15 hover:bg-white/25 font-bold px-3 py-1 rounded-full text-xs">Clear</button>
      </div>
      <div className="grid grid-cols-6 gap-1 max-w-[360px] mx-auto">
        {cells.map((c, i) => (
          <button
            key={i}
            onClick={() => setCells((cs) => cs.map((x, j) => j === i ? color : x))}
            style={{ backgroundColor: c }}
            className="aspect-square rounded"
          />
        ))}
      </div>
    </div>
  );
}

/* Spot the Odd — one emoji is different, find it */
function SpotTheOdd() {
  const POOLS = [["🐶", "🐺"], ["🐼", "🐨"], ["🐸", "🐢"], ["🦊", "🐱"], ["🐰", "🐹"]];
  const [round, setRound] = useState(0);
  const [grid, setGrid] = useState(() => makeOddGrid(POOLS[0]));
  const [score, setScore] = useState(0);
  const [msg, setMsg] = useState("");

  const tap = (i) => {
    if (grid[i].odd) {
      setScore((n) => n + 1); setMsg("Yes! 🎉");
      const next = (round + 1) % POOLS.length;
      setRound(next);
      setTimeout(() => { setGrid(makeOddGrid(POOLS[next])); setMsg(""); }, 500);
    } else { setMsg("Try again!"); setTimeout(() => setMsg(""), 400); }
  };

  return (
    <div data-testid="game-spot-board" className="text-center">
      <div className="text-white/80 text-sm">Which one is different? Score: <b className="text-yellow-300">{score}</b></div>
      {msg && <div className="mt-1 text-xl font-black">{msg}</div>}
      <div className="grid grid-cols-5 gap-2 mt-3 max-w-[360px] mx-auto">
        {grid.map((c, i) => (
          <button key={i} onClick={() => tap(i)} className="aspect-square text-3xl rounded-xl bg-white/10 hover:bg-white/20 transition">{c.emoji}</button>
        ))}
      </div>
    </div>
  );
}

/* Math Quick — simple add/subtract quiz */
function MathQuick() {
  const [q, setQ] = useState(() => makeMathQ());
  const [score, setScore] = useState(0);

  const pick = (n) => {
    if (n === q.answer) { setScore((s) => s + 1); setQ(makeMathQ()); }
    else setScore((s) => Math.max(0, s - 1));
  };

  return (
    <div data-testid="game-math-board" className="text-center">
      <div className="text-white/80 text-sm">Score: <b className="text-yellow-300">{score}</b></div>
      <div className="my-6 font-display font-black text-5xl">{q.a} {q.op} {q.b} = ?</div>
      <div className="grid grid-cols-4 gap-2 max-w-[320px] mx-auto">
        {q.choices.map((n, i) => (
          <button key={i} onClick={() => pick(n)} className="aspect-square text-3xl font-black rounded-xl bg-gradient-to-br from-rose-400 to-orange-500 hover:scale-105 transition">{n}</button>
        ))}
      </div>
    </div>
  );
}

/* Word Match — match word with picture emoji */
function WordMatch() {
  const PAIRS = [
    { word: "CAT", emoji: "🐱" }, { word: "DOG", emoji: "🐶" },
    { word: "SUN", emoji: "☀️" }, { word: "STAR", emoji: "⭐" },
    { word: "TREE", emoji: "🌳" }, { word: "FISH", emoji: "🐟" },
    { word: "MOON", emoji: "🌙" }, { word: "CAR", emoji: "🚗" },
  ];
  const [target, setTarget] = useState(() => PAIRS[Math.floor(Math.random() * PAIRS.length)]);
  const [opts, setOpts] = useState(() => shuffle(PAIRS).slice(0, 4));
  const [score, setScore] = useState(0);

  useEffect(() => {
    if (!opts.some((o) => o.word === target.word)) {
      setOpts([target, ...shuffle(PAIRS.filter((p) => p.word !== target.word)).slice(0, 3)].sort(() => Math.random() - 0.5));
    }
  }, [target, opts]);

  const pick = (o) => {
    if (o.word === target.word) {
      setScore((s) => s + 1);
      const next = PAIRS[Math.floor(Math.random() * PAIRS.length)];
      setTarget(next);
      setOpts([next, ...shuffle(PAIRS.filter((p) => p.word !== next.word)).slice(0, 3)].sort(() => Math.random() - 0.5));
    } else { setScore((s) => Math.max(0, s - 1)); }
  };

  return (
    <div data-testid="game-word-board" className="text-center">
      <div className="text-white/80 text-sm">Which picture matches this word? Score: <b className="text-yellow-300">{score}</b></div>
      <div className="my-4 font-display font-black text-5xl tracking-widest">{target.word}</div>
      <div className="grid grid-cols-4 gap-2 max-w-[320px] mx-auto">
        {opts.map((o, i) => (
          <button key={i} onClick={() => pick(o)} className="aspect-square text-5xl rounded-xl bg-white/10 hover:bg-white/20 transition">{o.emoji}</button>
        ))}
      </div>
    </div>
  );
}

/* Simon Says — watch the pattern, tap it back */
function SimonSays() {
  const COLORS = [
    { k: 0, c: "bg-red-500",     flash: "bg-red-300" },
    { k: 1, c: "bg-emerald-500", flash: "bg-emerald-300" },
    { k: 2, c: "bg-sky-500",     flash: "bg-sky-300" },
    { k: 3, c: "bg-yellow-500",  flash: "bg-yellow-300" },
  ];
  const [seq, setSeq] = useState([]);
  const [user, setUser] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [msg, setMsg] = useState("Tap Start!");
  const [score, setScore] = useState(0);

  const start = async () => {
    const next = [...seq, Math.floor(Math.random() * 4)];
    setSeq(next); setUser([]); setMsg("Watch…");
    for (let i = 0; i < next.length; i++) {
      await new Promise((r) => setTimeout(r, 500));
      setPlaying(next[i]);
      await new Promise((r) => setTimeout(r, 400));
      setPlaying(null);
    }
    setMsg("Your turn!");
  };

  const tap = (k) => {
    const pos = user.length;
    if (seq[pos] !== k) { setMsg("Oops! Game over."); setSeq([]); setUser([]); setScore(0); return; }
    const nu = [...user, k]; setUser(nu);
    if (nu.length === seq.length) { setScore(seq.length); setMsg("Nice! Next round…"); setTimeout(start, 600); }
  };

  return (
    <div data-testid="game-sim-board" className="text-center">
      <div className="text-white/80 text-sm">Score: <b className="text-yellow-300">{score}</b> — {msg}</div>
      <div className="grid grid-cols-2 gap-3 max-w-[280px] mx-auto mt-4">
        {COLORS.map((c) => (
          <button key={c.k} onClick={() => tap(c.k)} className={`aspect-square rounded-2xl transition ${playing === c.k ? c.flash : c.c} ring-2 ring-white/20`} />
        ))}
      </div>
      {seq.length === 0 && <button onClick={start} className="mt-5 bg-yellow-300 text-black font-black px-5 py-2 rounded-full">Start ▶</button>}
    </div>
  );
}

/* helpers */
function shuffle(arr) { return arr.sort(() => Math.random() - 0.5); }
function newStar() { return { id: Math.random(), x: Math.random() * 88 + 2, y: Math.random() * 88 + 2 }; }
function genStars() { return Array.from({ length: 8 }, newStar); }
function genGrid(pool) {
  const n = 25;
  return Array.from({ length: n }, () => pool[Math.floor(Math.random() * pool.length)]);
}
function makeOddGrid([normal, odd]) {
  const n = 15;
  const oddIdx = Math.floor(Math.random() * n);
  return Array.from({ length: n }, (_, i) => ({ emoji: i === oddIdx ? odd : normal, odd: i === oddIdx }));
}
function makeMathQ() {
  const ops = ["+", "-"];
  const op = ops[Math.floor(Math.random() * ops.length)];
  let a = Math.floor(Math.random() * 10) + 1;
  let b = Math.floor(Math.random() * 10) + 1;
  if (op === "-" && b > a) [a, b] = [b, a];
  const answer = op === "+" ? a + b : a - b;
  const choices = [answer, answer + 1, Math.max(0, answer - 1), answer + 2].sort(() => Math.random() - 0.5);
  return { a, b, op, answer, choices };
}
